// src/utils/ticketedEventsUi.js
// Pure helpers for the Eventbrite host-sales screens, kept free of React/MUI
// so they can be unit tested with plain node (src/utils/ticketedEventsUi.test.mjs).

export function isEventbritePayout(row) {
  return row?.payoutType === 'eventbrite';
}

// What "Recalculate" means for a payout row. Eventbrite commission has no
// guestlist/booth parts (all zero), so summing them would give $0: it resets
// to the amount the ticket sync worked out instead (Codex F3).
export function recalculatedTotal(row, componentTotal) {
  if (isEventbritePayout(row)) return Number(row?.eventbrite?.generatedTotalAmount) || 0;
  return componentTotal(row);
}

// The ONLY fields an admin save writes to an Eventbrite payout: total, status,
// source and the permanent sync lock. Never the guestlist/booth components.
// `stamp` is serverTimestamp() in the app (a plain value in tests).
export function eventbritePayoutUpdate(row, newStatus, stamp) {
  return {
    totalAmount: Number(row?.totalAmount || 0),
    status: newStatus ?? row?.status ?? 'pending',
    sources: ['eventbrite'],
    'eventbrite.syncLockedAt': stamp,
    updatedAt: stamp
  };
}

// Bulk approve/decline: Eventbrite rows also get the permanent sync lock (F12).
export function bulkStatusUpdate(row, status, stamp) {
  return {
    status,
    updatedAt: stamp,
    ...(isEventbritePayout(row) ? { 'eventbrite.syncLockedAt': stamp } : {})
  };
}

// Which image "Save flyer" saves, and what the card shows (F14): the story
// flyer if uploaded, else the Eventbrite image; the banner falls back to the
// flyer; a thumbnail of the flyer is shown when it differs from the banner.
export function flyerAssets(event) {
  const saveUrl = event?.storyImageUrl || event?.imageUrl || '';
  const bannerUrl = event?.imageUrl || saveUrl;
  return { saveUrl, bannerUrl, thumbUrl: saveUrl && saveUrl !== bannerUrl ? saveUrl : '' };
}

// Admin notice after Save, for every sync outcome the server can return.
export function saveSyncNotice(sync, name, isNew) {
  const done = `${isNew ? 'Added' : 'Saved'} ${name}`;
  const s = sync?.status;
  if (s === 'ok') return { severity: 'success', message: `${done}. Synced ${sync.tickets} tickets.` };
  if (s === 'error') return { severity: 'warning', message: `${done}, but the first sync failed: ${sync.message}` };
  if (s === 'skipped' || s === 'lost_lease') {
    return { severity: 'info', message: `${done}. Another sync was running, so the numbers will update within 15 minutes.` };
  }
  return { severity: 'warning', message: `${done}, but the sync didn't run (${s || 'no result'}). Press Sync now to retry.` };
}

// ── Eventbrite payouts in the rest of the portal (bell, reports, Pays) ──

// Retainer status from a host profile, as the rest of the app uses it.
export function profileOnRetainer(profile) {
  const n = Number(profile?.retainerAmount);
  return Number.isFinite(n) && n > 0;
}

// Retainer hosts are paid everything by bank transfer (owner 2026-10-03), so
// the bell must never tell them to collect ticket commission at reception.
// When the host's current profile is known it DECIDES, in both directions
// (a stale stored flag can't keep a now-cash host from collecting, or tell a
// now-retainer host to collect). The stored flag is used only when no
// profile is available (Codex R8/R9).
export function payoutHidesCollectionNotice(p, profile) {
  if (!isEventbritePayout(p)) return false;
  if (profile && typeof profile === 'object') return profileOnRetainer(profile);
  return p?.eventbrite?.hostOnRetainer === true;
}

// The bell's personal "ready for collection" notice for an Eventbrite payout
// needs the host's retainer status CONFIRMED by the server: true = retainer
// (no notice), false = cash (notice), null/undefined = not confirmed yet or
// offline (no notice yet: never risk telling a retainer host to collect).
// Weekly payouts are unaffected.
export function collectNoticeAllowed(p, serverOnRetainer) {
  if (!isEventbritePayout(p)) return true;
  return serverOnRetainer === false;
}

// Retainer status from a server read of the host's user doc: true/false only
// when the doc exists; a missing doc is NOT confirmation of cash (Codex R10).
export function retainerFromServerDoc(snap) {
  if (!snap || typeof snap.exists !== 'function' || !snap.exists()) return null;
  return profileOnRetainer(snap.data());
}

// Which server read of retainer status matches the profile as it is NOW: the
// profile id plus its retainer amount, so a retainer change on the SAME
// profile also needs a fresh answer (Codex R11).
export function retainerReadKey(profile) {
  if (!profile?.id) return null;
  return `${profile.id}|${String(profile.retainerAmount ?? '')}`;
}

// What the bell may use for the current host's personal payouts. Each async
// result is tagged with what it was loaded for, and anything loaded for
// another profile or an older retainer amount is ignored:
// payoutSnap = { id, payouts } from the payouts listener (tagged by profile id),
// serverRetainer = { key, onRetainer, settled } from the server read (tagged by
// retainerReadKey at the time of the read), readKey = retainerReadKey(profile) now.
// One shared empty list, so React memo deps stay stable while nothing is loaded.
const NO_PAYOUTS = Object.freeze([]);
export function personalPayoutView(profileId, payoutSnap, serverRetainer, readKey) {
  if (!profileId) return { loaded: true, settled: true, onRetainer: null, payouts: NO_PAYOUTS };
  const loaded = payoutSnap?.id === profileId;
  const settled = Boolean(readKey) && serverRetainer?.key === readKey && serverRetainer.settled === true;
  return {
    loaded,
    settled,
    onRetainer: settled ? (serverRetainer.onRetainer ?? null) : null,
    payouts: loaded && payoutSnap.payouts ? payoutSnap.payouts : NO_PAYOUTS
  };
}

// Keeps an already-open payment pop-up in step with what is eligible now:
// items that are no longer eligible are dropped (and the pop-up closes if
// none are left).
export function reconcilePopupItems(popupItems, currentItems) {
  const live = new Set((currentItems || []).map((n) => n.id));
  return (popupItems || []).filter((n) => live.has(n.id));
}

// Bell text that says where the money is from.
export function paymentNoticeBody(p, money, day) {
  if (isEventbritePayout(p)) {
    const name = p?.eventbrite?.eventName ? ` for ${p.eventbrite.eventName}` : '';
    return `Ticket sales commission: ${money}${name} (${day}) is ready for collection.`;
  }
  return `${money} for ${day} is now ready for collection.`;
}

// Payout totals for a night, with Eventbrite ticket commission shown apart.
export function payoutTotalsBySource(payouts) {
  const out = { total: 0, eventbriteTotal: 0, eventbriteRows: 0, otherTotal: 0 };
  for (const p of payouts || []) {
    const amt = Number(p?.totalAmount) || 0;
    out.total += amt;
    if (isEventbritePayout(p)) {
      out.eventbriteTotal += amt;
      out.eventbriteRows += 1;
    } else {
      out.otherTotal += amt;
    }
  }
  return out;
}

// Pays-page label for an Eventbrite commission row (profile = the viewing
// host's own, current profile; the stored flag is a fallback).
export function eventbritePaysLabel(row, profile) {
  if (!isEventbritePayout(row)) return '';
  const eb = row.eventbrite || {};
  const tickets = Number(eb.paidTickets) || 0;
  const onRetainer = profile && typeof profile === 'object' ? profileOnRetainer(profile) : eb.hostOnRetainer === true;
  const how = onRetainer ? ', paid by bank transfer' : '';
  return `Ticket sales: ${eb.eventName || 'Eventbrite event'} (${tickets} paid ticket${tickets === 1 ? '' : 's'}${how})`;
}

// ── Host page text (owner 2026-10-04) ──

// Starting wording for a new event's host page text (admins can change it).
export const DEFAULT_HOST_INTRO =
  'If you would like to be involved, please promote the event across your socials, group chats and networks to maximise ticket sales and reach.';
export const DEFAULT_HOST_CLOSING =
  'Please make sure you use your unique host link when promoting. All ticket sales through your link are automatically tracked and allocated to you.\nLet us know if you need any extra assets or support.';

function dollars(cents) {
  const n = Number(cents) || 0;
  return `$${(n / 100).toFixed(n % 100 ? 2 : 0)}`;
}

// The same wording the server uses for a reward (rewardDescription).
export function rewardOfferText({ discountType, percentOff, amountOffCents } = {}) {
  if (discountType === 'amount') return `${dollars(amountOffCents)} off a ticket`;
  if (Number(percentOff) >= 100) return 'a free ticket';
  return `${Number(percentOff) || 0}% off a ticket`;
}

// "This event includes" points: the fixed ones come from the event's live
// settings, so they change when an admin changes commission or the reward.
// reward = { minPaidTickets, description } while a reward is on and the host
// hasn't been sent a code yet (the reward box shows the code otherwise).
export function hostPageBullets({ commission, reward, hasFlyer, hasVideo, points } = {}) {
  const out = ['Your unique host tracking link'];
  const per = Number(commission?.perTicketCents) || 0;
  const min = Number(commission?.minPaidTickets) || 0;
  if (per > 0) out.push(`Receive ${dollars(per)} per ticket sold${min > 1 ? ` (min ${min} tickets)` : ''}`);
  const need = Number(reward?.minPaidTickets) || 0;
  if (reward && need > 0 && reward.description) {
    out.push(`Sell ${need} paid ticket${need === 1 ? '' : 's'} and get ${reward.description} for yourself`);
  }
  for (const p of points || []) if (String(p).trim()) out.push(String(p).trim());
  if (hasFlyer) out.push('Downloadable event flyers for easy sharing');
  if (hasVideo) out.push('A promo video to post and share');
  return out;
}

// Same limits and cleaning as the server (functions/ticketedEventsLogic.js
// normaliseHostPage), so the admin preview shows exactly what will be saved.
export const HOST_PAGE_LIMITS = { headline: 200, intro: 4000, point: 200, points: 12, closing: 2000 };

function cleanHostText(v, max, multiLine) {
  let s = String(v ?? '').replace(/\r\n?/g, '\n');
  s = multiLine
    ? s
        .replace(/[\u0000-\u0009\u000B-\u001F\u007F]/g, '')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
    : s.replace(/[\u0000-\u001F\u007F]+/g, ' ').replace(/\s{2,}/g, ' ');
  return s.trim().slice(0, max).trim();
}

export function normaliseHostPageText(x) {
  const h = x && typeof x === 'object' ? x : {};
  const rawPoints = Array.isArray(h.points) ? h.points : String(h.points ?? '').split(/\r?\n/);
  return {
    headline: cleanHostText(h.headline, HOST_PAGE_LIMITS.headline, false),
    intro: cleanHostText(h.intro, HOST_PAGE_LIMITS.intro, true),
    points: rawPoints
      .map((p) => cleanHostText(p, HOST_PAGE_LIMITS.point, false))
      .filter(Boolean)
      .slice(0, HOST_PAGE_LIMITS.points),
    closing: cleanHostText(h.closing, HOST_PAGE_LIMITS.closing, true)
  };
}

// Warning for the extra points box when some lines won't be kept.
// Judged on the cleaned lines (as saved), not the raw text (R13).
export function hostPointsProblem(text) {
  const lines = String(text ?? '')
    .split(/\r?\n/)
    .map((l) => cleanHostText(l, Infinity, false))
    .filter(Boolean);
  const tooMany = lines.length > HOST_PAGE_LIMITS.points;
  const tooLong = lines.some((l) => l.length > HOST_PAGE_LIMITS.point);
  if (!tooMany && !tooLong) return '';
  const parts = [];
  if (tooMany) parts.push(`only the first ${HOST_PAGE_LIMITS.points} points are kept (you have ${lines.length})`);
  if (tooLong) parts.push(`points over ${HOST_PAGE_LIMITS.point} characters are cut short`);
  const msg = parts.join(', and ');
  return `${msg.charAt(0).toUpperCase()}${msg.slice(1)}. The preview shows what will be saved.`;
}

// How the flyer buttons behave on each device (owner 2026-10-04: Android's
// share sheet has no obvious "save" option).
// - Computer: one "Save flyer" button that downloads the image.
// - iPhone: one "Save flyer" button that opens the share sheet, whose
//   "Save Image" puts it in Photos (a download would only reach Files).
// - Android / other phones: "Save to phone" downloads straight to the phone,
//   plus "Share flyer" for the share sheet (Instagram, WhatsApp...).
export function flyerActions({ phone, ios, canShareFiles }) {
  if (!phone) return { saveLabel: 'Save flyer', save: 'download', share: false };
  if (ios) return { saveLabel: 'Save flyer', save: canShareFiles ? 'share' : 'download', share: false };
  return { saveLabel: 'Save to phone', save: 'download', share: Boolean(canShareFiles) };
}

// ── Event tabs on Promote & Earn (owner 2026-10-04) ──

// Which tab opens first: the host's own brand's event that is still on,
// then any event still on, then the first one.
export function defaultEventTab(events) {
  const list = events || [];
  const live = (e) => e && e.promoted && !e.ended;
  const pick = list.find((e) => live(e) && e.isOwnBrand) || list.find(live) || list[0];
  return pick ? pick.id : null;
}

// The event shown as the banner at the top of the Dashboard (owner 2026-10-13).
// Only one, and only one still on and being promoted. The brand picks the mode
// on its Brand page (meta/uiSettings.promoteBannerMode):
//   'own'            this brand's own event only (default; keeps it uncluttered)
//   'ownThenPartner' own event, else the first partner event in Promote & Earn order
//   'off'            no banner
// null = no banner.
export const PROMOTE_BANNER_MODES = ['own', 'ownThenPartner', 'off'];

export function normalisePromoteBannerMode(v) {
  return PROMOTE_BANNER_MODES.includes(v) ? v : 'own';
}

// Only what the banner needs (Codex dashboard R1 #2): the Dashboard keeps this
// in memory, never the host's sales or buyer names.
export function bannerEventView(e) {
  return {
    id: String(e?.id || ''),
    name: String(e?.name || ''),
    imageUrl: e?.imageUrl || '',
    storyImageUrl: e?.storyImageUrl || '',
    promoted: e?.promoted === true,
    ended: e?.ended === true,
    isOwnBrand: e?.isOwnBrand === true
  };
}

export function dashboardPromoEvent(events, mode = 'own') {
  const m = normalisePromoteBannerMode(mode);
  if (m === 'off') return null;
  const live = (events || []).filter((e) => e && e.promoted && !e.ended);
  return live.find((e) => e.isOwnBrand) || (m === 'ownThenPartner' ? live[0] : null) || null;
}

// A finger swipe on the event: +1 = next tab (swipe left), -1 = previous,
// 0 = not a deliberate sideways swipe (too short, or mostly up/down scrolling).
export function swipeStep(dx, dy, minPx = 60) {
  const ax = Math.abs(Number(dx) || 0);
  const ay = Math.abs(Number(dy) || 0);
  if (ax < minPx || ax < 1.5 * ay) return 0;
  return dx < 0 ? 1 : -1;
}

// ── "View as" page state (Codex view-as review V4) ──
// Every response is tagged with the identity it was loaded for; the page only
// shows data whose tag matches who is selected NOW, so one host's link or
// sales can never appear under another host's name, even for a single render.
export function viewKey(viewAsId) {
  return viewAsId ? `as:${viewAsId}` : 'self';
}

export function pageView(state, key) {
  if (!state || state.key !== key || state.loading) return { mode: 'loading' };
  if (state.error) return { mode: 'error', error: state.error };
  return {
    mode: 'ready',
    events: Array.isArray(state.events) ? state.events : [],
    notStarted: state.notStarted === true,
    viewingAs: state.viewingAs || null
  };
}

// ── Promo videos (owner 2026-10-05). Same limits as storage.rules and the server. ──
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024;
export const VIDEO_MAX_PER_EVENT = 5;
const VIDEO_TYPES = { mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime', webm: 'video/webm' };

export function formatBytes(n) {
  const b = Number(n) || 0;
  if (b >= 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(b >= 100 * 1024 * 1024 ? 0 : 1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
}

// Checks a chosen file before uploading. ok=false gives the reason; a .mov
// gets a warning because some Android phones and Chrome can't play it.
export function videoUploadCheck(file, existingCount = 0) {
  const name = String(file?.name || '');
  const ext = (name.split('.').pop() || '').toLowerCase();
  const contentType = VIDEO_TYPES[ext];
  if (existingCount >= VIDEO_MAX_PER_EVENT) {
    return { ok: false, error: `An event can have up to ${VIDEO_MAX_PER_EVENT} videos. Remove one first.` };
  }
  if (!contentType) return { ok: false, error: 'Choose an MP4, MOV, M4V or WebM video.' };
  const size = Number(file?.size) || 0;
  if (size <= 0) return { ok: false, error: 'That file is empty.' };
  if (size > VIDEO_MAX_BYTES)
    return { ok: false, error: `That video is ${formatBytes(size)}. The limit is 200 MB; export a smaller version.` };
  const warning = ext === 'mov' ? 'MOV files from iPhone may not play on some Android phones or in Chrome. MP4 plays everywhere.' : '';
  return { ok: true, ext, contentType, warning };
}

// How the Save / Share buttons behave for a video.
// Computers: a plain download.
// iPhone: "Get video" loads it into the page, then "Save video" opens the
//   share sheet from a fresh tap (that's how a video gets into Photos).
// Android (owner 2026-10-05): both buttons straight away. "Save to phone"
//   hands the file to the phone's download manager; "Share video" loads it and
//   opens the share sheet in the same tap (or asks for one more tap if loading
//   took longer than the browser allows).
export function videoActions({ phone, ios, canShareFiles }) {
  if (!phone) return { mode: 'download' };
  if (ios) return { mode: 'prepare', save: canShareFiles ? 'share' : 'open', share: false };
  return { mode: 'direct', share: Boolean(canShareFiles) };
}
